import { capitalize, MuiAlert, Spin, strIsNullOrEmpty } from '@fox/atoms';
import { ResponsiveTable, SortState } from '@fox/data-grid';
import { useSubscribeTopicWithHandler } from '@fox/extensions';
import { FlexWrapRowSpacedCentered } from '@fox/layout';
import { useState } from 'react';
import { useContentContext } from '../../../content-context';
import { useFinancialContext } from '../../../financial-config-context';
import { useInvoices } from '../../../Membership/query-hooks/query-implementations';
import { useTableSettingsContext } from '../../../table-settings-context';
import { REFETCH_INVOICES } from '../../../topics';
import { InvoiceStatus, SwiftFoxInvoice } from '../contracts';
import { TableWrapper } from '../receipts';
import { formatCurrency, InvoiceStatusChip, InvoiceStatusIcon, renderDate } from '../shared';
import { InvoceDetailedMobile, InvoiceDetailedDesktop } from './invoice-details';
import { PayInvoiceDesktop, PayInvoiceMobile } from './pay-invoice';

function canPayInvoice(status: InvoiceStatus) {
	//not sure how to deal with partly paid invoices atm
	if (status === InvoiceStatus.DRAFT || status === InvoiceStatus.SUBMITTED || status === InvoiceStatus.AUTHORISED) return true;
	return false;
}
export function InvoicesTable({
	useLinkedOrganisationData,
	allowedProviders,
	rowsExpandedOnSmallScreens
}: {
	useLinkedOrganisationData: boolean;
	rowsExpandedOnSmallScreens: boolean;
	allowedProviders?: string[];
}) {
	const { requestToken, contentId } = useContentContext();
	const { currencies } = useFinancialContext();
	const { error: tableSettingsError, getTableSettings, loading: tableSettingsLoading } = useTableSettingsContext();
	const [sort, setSort] = useState<SortState>({ column: 'createdOn', asc: false });
	const [page, setPage] = useState<number>(1);
	const PAGE_SIZE = 10;
	const { error, data, loading, refetch } = useInvoices({
		requestToken,
		contentId,
		page,
		pageSize: PAGE_SIZE,
		sort,
		useLinkedOrganisationData,
		triggers: ['sort', 'page']
	});

	useSubscribeTopicWithHandler(REFETCH_INVOICES, () => refetch());

	if (!strIsNullOrEmpty(error)) return <MuiAlert severity="error">{error}</MuiAlert>;
	if (!strIsNullOrEmpty(tableSettingsError)) return <MuiAlert severity="error">{tableSettingsError}</MuiAlert>;

	const tableSettings = getTableSettings('Invoices');

	if (!tableSettings) return <></>;

	const columns = [
		{
			title: 'Date',
			dataIndex: 'date',
			render: record => {
				return renderDate(record.date);
			},
			sortable: true,
			showOnMobileRow: true
		},
		{
			title: 'Invoice no',
			dataIndex: 'invoiceNumber',
			render: record => record.integrationInvoiceNumber ?? record.id
		},
		{
			title: 'Total',
			dataIndex: 'totalAmount',
			render: record => {
				return formatCurrency(record.totalAmount, record.currency ?? currencies.default);
			}
		},
		{
			title: 'Method',
			dataIndex: 'integrationType',
			render: record => <span>{!strIsNullOrEmpty(record.integrationType) ? capitalize(record.integrationType) : 'N/A'}</span>
		},
		{
			title: 'Status',
			dataIndex: 'status',
			render: record => <InvoiceStatusChip status={record.status} />,
			mobileHeaderRender: record => <InvoiceStatusIcon status={record.status} />,
			showOnMobileRow: true
		},
		{ title: 'Actions', isActionColumn: true }
	];

	const columnsToUse = columns.filter(c => {
		const columnSettings = tableSettings.columnSettings.find(s => s.dataIndex === c.dataIndex);
		if (c.isActionColumn) {
			return true;
		} else {
			return columnSettings.isVisible;
		}
	});

	return (
		<TableWrapper>
			<Spin spinning={loading || !data}>
				{data?.items?.length === 0 ? (
					<MuiAlert severity="info">No invoice data to display</MuiAlert>
				) : (
					<ResponsiveTable<SwiftFoxInvoice>
						data={data?.items}
						rowsExpandedOnSmallScreens={rowsExpandedOnSmallScreens}
						sortProps={{ onSort: sortState => setSort(sortState), sort }}
						paginationProps={
							data?.totalResults
								? {
										page,
										onPageChange: page => setPage(page),
										total: data?.totalResults,
										itemsPerPage: PAGE_SIZE
								  }
								: undefined
						}
						rowActionComponent={record => (
							<FlexWrapRowSpacedCentered>
								<InvoiceDetailedDesktop record={record} />
								{canPayInvoice(record.status) ? (
									<PayInvoiceDesktop
										record={record}
										useLinkedOrganisationData={useLinkedOrganisationData}
										allowedProviders={allowedProviders}
									/>
								) : null}
							</FlexWrapRowSpacedCentered>
						)}
						mobileRowActionComponent={record => (
							<FlexWrapRowSpacedCentered>
								<InvoceDetailedMobile record={record} />
								{canPayInvoice(record.status) ? (
									<PayInvoiceMobile
										record={record}
										useLinkedOrganisationData={useLinkedOrganisationData}
										allowedProviders={allowedProviders}
									/>
								) : null}
							</FlexWrapRowSpacedCentered>
						)}
						columns={columnsToUse}
					/>
				)}
			</Spin>
		</TableWrapper>
	);
}
